import { useRef, useCallback, useState, memo, useMemo } from 'react';
import { Box, Slider, Stack, IconButton, Typography, Tooltip } from '@mui/material';
import Iconify from '../../../components/iconify';
import { useTimeline, useTimelineControls } from './TimelineContext';
import { buildCollapsedTimeline, collapsedPercent, realToCollapsed, collapsedToReal, snapToNearestRecordingBoundary } from './gapCollapse';

function formatDuration(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatClockTime(epochMs) {
  return new Date(epochMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatFullDateTime(epochMs) {
  const d = new Date(epochMs);
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}  ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

/**
 * Isolated play/pause button that subscribes only to TimelineControlContext.
 * This prevents the 20 Hz currentTime ticks from re-rendering the button,
 * which was the primary cause of dropped clicks during playback.
 *
 * Uses onPointerDown instead of onClick so the action fires on the initial
 * press event rather than waiting for the full click cycle (pointerdown →
 * pointerup → click). During heavy re-renders, React can reconcile the DOM
 * between pointerup and click, discarding the synthetic click event.
 */
const PlayPauseButton = memo(() => {
  const { isPlaying, play, pause } = useTimelineControls();

  const handlePointerDown = useCallback((e) => {
    // Only respond to primary button (left click / touch)
    if (e.button !== 0) return;
    // Prevent default to avoid focus-stealing and double-fire with onClick
    e.preventDefault();
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  return (
    <IconButton
      onPointerDown={handlePointerDown}
      size="medium"
      sx={{ position: 'relative', zIndex: 10 }}
    >
      <Iconify icon={isPlaying ? 'eva:pause-circle-fill' : 'eva:play-circle-fill'} width={40} />
    </IconButton>
  );
});

export default function UnifiedTimeline({
  recordings,
  eventsMap = {},
  globalMarkers = [],
  tabMarkers = [],
  activeTabId,
  allTabMarkers = [], // [{ tabId, tabName, markers[] }]
  onSwitchToTab,
  recordingMetadata = {},
  // Legacy prop
  timelineMarkers,
}) {
  const { currentTime, isPlaying, seek, play, pause, bounds, window: timeWindow, setWindow, clearWindow } = useTimeline();
  const sliderRef = useRef(null);
  const barRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [timeMode, setTimeMode] = useState('clock');
  const [showAllTabMarkers, setShowAllTabMarkers] = useState(false);

  const allEvents = Object.values(eventsMap).flat();

  // Build collapsed timeline — memoized on recordings identity
  const collapsedTimeline = useMemo(
    () => buildCollapsedTimeline(recordings),
    [recordings]
  );
  const { segments, totalCollapsedDuration } = collapsedTimeline;

  // Real epoch time -> percentage of the collapsed bar, or null when the time
  // is off the timeline entirely.
  const toCollapsedPct = useCallback(
    (time) => collapsedPercent(time, collapsedTimeline),
    [collapsedTimeline]
  );

  // Use legacy prop if new props not provided
  const effectiveGlobalMarkers = globalMarkers.length > 0 ? globalMarkers : (timelineMarkers || []);
  const effectiveTabMarkers = tabMarkers || [];

  // Collect other-tab markers when show-all is enabled
  const otherTabMarkers = showAllTabMarkers
    ? allTabMarkers
        .filter((t) => t.tabId !== activeTabId)
        .flatMap((t) => t.markers.map((m) => ({ ...m, tabId: t.tabId, tabName: t.tabName })))
    : [];

  // Playhead position in collapsed space
  const collapsedPlayhead = realToCollapsed(currentTime, collapsedTimeline);
  const playheadPct = totalCollapsedDuration > 0 && collapsedPlayhead !== null
    ? (collapsedPlayhead / totalCollapsedDuration) * 100
    : 0;

  // Slider operates in collapsed time space
  const collapsedSliderValue = realToCollapsed(currentTime, collapsedTimeline) ?? 0;

  const handleSliderChange = useCallback((_, value) => {
    const realTime = collapsedToReal(value, collapsedTimeline);
    if (realTime !== null) {
      // Snap to nearest recording boundary when seeking into a gap
      const snapped = snapToNearestRecordingBoundary(realTime, collapsedTimeline);
      seek(snapped);
    }
  }, [seek, collapsedTimeline]);

  const posToTime = useCallback((clientX) => {
    if (!barRef.current) return bounds.start;
    const rect = barRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const collapsedTime = pct * totalCollapsedDuration;
    const realTime = collapsedToReal(collapsedTime, collapsedTimeline);
    if (realTime === null) return bounds.start;
    // Snap to nearest recording boundary when clicking into a gap
    return snapToNearestRecordingBoundary(realTime, collapsedTimeline);
  }, [bounds, totalCollapsedDuration, collapsedTimeline]);

  // Window selection temporarily disabled
  // const handleBarMouseDown = useCallback((e) => {
  //   if (e.button !== 0) return;
  //   const time = posToTime(e.clientX);
  //   setWindow({ start: time, end: time });
  //   setDragging('end');
  //   const handleMove = (me) => {
  //     const t = posToTime(me.clientX);
  //     setWindow((prev) => {
  //       if (!prev) return { start: time, end: t };
  //       return { start: Math.min(time, t), end: Math.max(time, t) };
  //     });
  //   };
  //   const handleUp = () => {
  //     setDragging(null);
  //     document.removeEventListener('mousemove', handleMove);
  //     document.removeEventListener('mouseup', handleUp);
  //     setWindow((prev) => {
  //       if (!prev || prev.end - prev.start < 1000) return null;
  //       return prev;
  //     });
  //   };
  //   document.addEventListener('mousemove', handleMove);
  //   document.addEventListener('mouseup', handleUp);
  // }, [posToTime, setWindow]);

  const windowStartPct = timeWindow ? toCollapsedPct(timeWindow.start) ?? 0 : 0;
  const windowEndPct = timeWindow ? toCollapsedPct(timeWindow.end) ?? 0 : 0;
  const windowLeft = windowStartPct;
  const windowWidth = windowEndPct - windowStartPct;
  const windowDuration = timeWindow ? timeWindow.end - timeWindow.start : totalCollapsedDuration;

  const renderMarkerPin = (marker, i, { opacity = 1, outlined = false, onClick, tooltipExtra = '' }) => {
    const left = toCollapsedPct(marker.time);
    // A marker whose recording has been removed has nowhere to sit. Drawing it
    // at 0 piled every such marker onto the left edge, where clicking one
    // seeked to the start of the timeline.
    if (left === null) return null;
    const color = marker.color || '#1976d2';
    const tooltipText = `${tooltipExtra}${marker.text || marker.label}`;

    return (
      <Tooltip key={`marker-${i}-${opacity}`} title={tooltipText} arrow placement="top">
        <Box
          sx={{
            position: 'absolute', left: `${left}%`, top: 0,
            width: 2, height: '100%', bgcolor: color, zIndex: 4,
            cursor: 'pointer', pointerEvents: 'auto',
            opacity,
            '&::after': {
              content: '""', position: 'absolute', top: -4, left: -3,
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: outlined ? 'transparent' : color,
              border: outlined ? `2px solid ${color}` : 'none',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (onClick) {
              onClick();
            } else {
              seek(marker.time);
            }
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {/* Play/Pause button — isolated from currentTime re-renders */}
        <PlayPauseButton />

        {/* Start time label */}
        <Tooltip title="Click to toggle: clock / elapsed" placement="top">
          <Typography
            variant="caption"
            sx={{ minWidth: timeMode === 'clock' ? 80 : 36, fontFamily: 'monospace', cursor: 'pointer', userSelect: 'none', textAlign: 'center', '&:hover': { color: 'primary.main' } }}
            onClick={() => setTimeMode((m) => m === 'clock' ? 'elapsed' : 'clock')}
          >
            {timeMode === 'clock' ? formatClockTime(currentTime) : formatDuration(collapsedSliderValue)}
          </Typography>
        </Tooltip>

        {/* Center column: colored bar stacked above slider */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Recording segments bar */}
          <Box
            ref={barRef}
            sx={{
              position: 'relative', height: 24, bgcolor: 'grey.100', borderRadius: 1,
              userSelect: 'none', mb: 0.5,
            }}
          >
            {/* Recording segments (collapsed positions) */}
            {segments.filter((seg) => seg.type === 'recording').map((seg) => {
              const left = totalCollapsedDuration > 0
                ? (seg.collapsedStart / totalCollapsedDuration) * 100 : 0;
              const width = totalCollapsedDuration > 0
                ? ((seg.collapsedEnd - seg.collapsedStart) / totalCollapsedDuration) * 100 : 0;
              // Find matching recording for metadata lookup
              const rec = recordings.find((r) => r.startTime >= seg.realStart && r.startTime <= seg.realEnd);
              const meta = rec ? recordingMetadata[rec.id] : undefined;
              const color = meta?.color || '#1976d2';
              const displayName = meta?.label || rec?.name || '';
              return (
                <Box
                  key={`rec-seg-${seg.collapsedStart}`}
                  sx={{
                    position: 'absolute', left: `${left}%`, width: `${width}%`,
                    height: '100%', bgcolor: color, borderRadius: 1, opacity: 0.6,
                    pointerEvents: 'none',
                  }}
                  title={displayName}
                />
              );
            })}

            {/* Gap segments (thin gray dividers) */}
            {segments.filter((seg) => seg.type === 'gap').map((seg) => {
              const left = totalCollapsedDuration > 0
                ? (seg.collapsedStart / totalCollapsedDuration) * 100 : 0;
              const width = totalCollapsedDuration > 0
                ? ((seg.collapsedEnd - seg.collapsedStart) / totalCollapsedDuration) * 100 : 0;
              return (
                <Box
                  key={`gap-seg-${seg.collapsedStart}`}
                  sx={{
                    position: 'absolute', left: `${left}%`, width: `${Math.max(width, 0.3)}%`,
                    height: '100%', bgcolor: 'grey.300', opacity: 0.8,
                    pointerEvents: 'none',
                  }}
                />
              );
            })}

            {/* Event markers (collapsed positions) */}
            {allEvents.map((evt, i) => {
              const left = toCollapsedPct(evt.startTime);
              const endPct = toCollapsedPct(evt.endTime);
              const width = Math.max(endPct - left, 0.3);
              return (
                <Tooltip key={`evt-${i}`} title={evt.label} arrow placement="top">
                  <Box
                    sx={{
                      position: 'absolute', left: `${left}%`, width: `${width}%`,
                      height: '100%', bgcolor: 'error.main', opacity: 0.8, zIndex: 2, pointerEvents: 'none',
                    }}
                  />
                </Tooltip>
              );
            })}

            {/* Global markers */}
            {effectiveGlobalMarkers.map((marker, i) =>
              renderMarkerPin(marker, `global-${i}`, { opacity: 1, outlined: false })
            )}

            {/* Current tab markers */}
            {effectiveTabMarkers.map((marker, i) =>
              renderMarkerPin(marker, `tab-${i}`, { opacity: 1, outlined: false })
            )}

            {/* Other-tab markers */}
            {otherTabMarkers.map((marker, i) =>
              renderMarkerPin(marker, `other-${i}`, {
                opacity: 0.4,
                outlined: true,
                tooltipExtra: `[${marker.tabName}] `,
                onClick: () => {
                  seek(marker.time);
                  onSwitchToTab?.(marker.tabId);
                },
              })
            )}

            {/* Playhead indicator */}
            <Box
              sx={{
                position: 'absolute',
                left: `${playheadPct}%`,
                top: -2,
                width: 0,
                height: 'calc(100% + 4px)',
                zIndex: 6,
                pointerEvents: 'none',
                // Vertical line
                borderLeft: '2px solid',
                borderColor: 'primary.main',
                // Triangle head
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -4,
                  left: -5,
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '5px solid',
                  borderTopColor: 'primary.main',
                },
              }}
            />

            {/* Window selection overlay — temporarily disabled */}
          </Box>

          {/* Slider — operates in collapsed time space */}
          <Slider
            ref={sliderRef}
            value={collapsedSliderValue}
            min={0}
            max={totalCollapsedDuration || 1}
            step={Math.max(1, Math.floor((totalCollapsedDuration || 1) / 10000))}
            onChange={handleSliderChange}
            size="small"
            sx={{ p: 0 }}
          />
        </Box>

        {/* End time label */}
        <Tooltip title="Click to toggle: clock / elapsed" placement="top">
          <Typography
            variant="caption"
            sx={{ minWidth: timeMode === 'clock' ? 80 : 36, fontFamily: 'monospace', cursor: 'pointer', userSelect: 'none', textAlign: 'center', '&:hover': { color: 'primary.main' } }}
            onClick={() => setTimeMode((m) => m === 'clock' ? 'elapsed' : 'clock')}
          >
            {timeMode === 'clock' ? formatClockTime(bounds.end) : formatDuration(totalCollapsedDuration)}
          </Typography>
        </Tooltip>

        {/* Show all tab markers toggle */}
        <Tooltip title={showAllTabMarkers ? 'Hide other tabs\' markers' : 'Show all tabs\' markers'} placement="top">
          <IconButton
            size="small"
            onClick={() => setShowAllTabMarkers((v) => !v)}
            color={showAllTabMarkers ? 'primary' : 'default'}
          >
            <Iconify icon={showAllTabMarkers ? 'eva:eye-fill' : 'eva:eye-off-fill'} width={18} />
          </IconButton>
        </Tooltip>

        {/* Window info — temporarily disabled */}
      </Stack>

      {/* Full date + time display */}
      <Typography variant="h4" sx={{ textAlign: 'center', fontFamily: 'monospace', color: 'text.primary', py: 0.5 }}>
        {formatFullDateTime(currentTime)}
      </Typography>
    </Box>
  );
}
